import { get, map, mapValues } from 'lodash'
import { TFunction } from 'i18next'
import { getReq, postReq, putReq } from '../../utils/request'
import { IResponsePagination } from '../../types/interfaces'
import { ThunkResult } from '../index'
import {
	CREATE_TELEPHONE_SURVEY,
	LOAD_SELECT_TELEPHONE_SURVEYS,
	LOAD_TELEPHONE_SURVEY
} from './types'

export enum TELEPHONE_SURVEY_CALL_STATUS {
	ANSWERED = 'ANSWERED',
	NO_ANSWER = 'NO_ANSWER',
	CALL_LATER = 'CALL_LATER',
}

export enum TELEPHONE_SURVEY_SATISFACTION {
	SATISFIED = 'SATISFIED',
	UNSATISFIED = 'UNSATISFIED',
}

export const translateTelephoneSurveyCallStatus = (status: TELEPHONE_SURVEY_CALL_STATUS, t: TFunction) => {
	switch (status) {
		case TELEPHONE_SURVEY_CALL_STATUS.ANSWERED:
			return t('pages:Zdvihol')
		case TELEPHONE_SURVEY_CALL_STATUS.NO_ANSWER:
			return t('pages:Nedvíhal')
		case TELEPHONE_SURVEY_CALL_STATUS.CALL_LATER:
			return t('pages:Zavolať neskôr')
		default:
			return status
	}
}

export const translateTelephoneSurveyCallStatusColor = (status: TELEPHONE_SURVEY_CALL_STATUS) => {
	switch (status) {
		case TELEPHONE_SURVEY_CALL_STATUS.ANSWERED:
			return 'green'
		case TELEPHONE_SURVEY_CALL_STATUS.NO_ANSWER:
			return 'orange'
		case TELEPHONE_SURVEY_CALL_STATUS.CALL_LATER:
			return 'blue'
		default:
			return 'default'
	}
}

export const translateTelephoneSurveySatisfaction = (status: TELEPHONE_SURVEY_SATISFACTION | null, t: TFunction) => {
	switch (status) {
		case TELEPHONE_SURVEY_SATISFACTION.SATISFIED:
			return t('pages:Spokojný')
		case TELEPHONE_SURVEY_SATISFACTION.UNSATISFIED:
			return t('pages:Nespokojný')
		default:
			return t('pages:Bez hodnotenia')
	}
}

export const translateTelephoneSurveySatisfactionColor = (status: TELEPHONE_SURVEY_SATISFACTION | null) => {
	switch (status) {
		case TELEPHONE_SURVEY_SATISFACTION.SATISFIED:
			return 'gold'
		case TELEPHONE_SURVEY_SATISFACTION.UNSATISFIED:
			return 'red'
		default:
			return 'default'
	}
}

export interface ITelephoneSurveyOriginalItem {
	id: number
	operatorID: number
	callStatus: TELEPHONE_SURVEY_CALL_STATUS
	satisfaction: TELEPHONE_SURVEY_SATISFACTION | null
	note: string | null
	calledAt: string | null
	scheduledCallAt: string | null
	createdAt: string | null
	trusteeName?: string | null
	trusteePhone?: string | null
	trusteeUserID?: number | null
	jobIDs: number[]
	jobs: { id: number, address: string }[]
	organization?: { id: number, name: string } | null
	cleaners: { id: number, name: string }[]
	operator: { id: number, name: string } | null
}

export interface ITelephoneSurveyTableItem {
	id: number
	jobAddresses: string
	cleanerNames: string
	operatorName: string
	callStatus: TELEPHONE_SURVEY_CALL_STATUS
	satisfaction: TELEPHONE_SURVEY_SATISFACTION | null
	note: string
	calledAt: string | null
	scheduledCallAt: string | null
	trusteeName?: string | null
	trusteePhone?: string | null
	trusteeUserID?: number | null
	organizationName?: string | null
	jobIDs: number[]
	jobs: { id: number, address: string }[]
	cleaners: { id: number, name: string }[]
}

export interface ITelephoneSurveyPayload {
	data: ITelephoneSurveyOriginalItem | null
}

export interface ITelephoneSurveysPayload {
	tableData: ITelephoneSurveyTableItem[]
	originalData: ITelephoneSurveyOriginalItem[]
	pagination: IResponsePagination | null
}

interface IGetTelephoneSurveyAction {
	type: LOAD_TELEPHONE_SURVEY
	payload: ITelephoneSurveyPayload
}

interface IGetTelephoneSurveysAction {
	type: LOAD_SELECT_TELEPHONE_SURVEYS
	payload: ITelephoneSurveysPayload
}

export type ITelephoneSurveyActions = IGetTelephoneSurveyAction | IGetTelephoneSurveysAction

export interface ITelephoneSurveyQueryParams {
	limit?: number | null
	page?: number | null
	search?: string | null
	startDate?: string | null
	endDate?: string | null
	jobs?: number[]
	employees?: number[]
	operators?: number[]
	callStatus?: TELEPHONE_SURVEY_CALL_STATUS | null
	satisfaction?: TELEPHONE_SURVEY_SATISFACTION | null
	hasPhone?: boolean
	orderBy?: string | null
	orderDirection?: string | null
}

export interface ITelephoneSurveyForm {
	jobIDs: number[]
	callStatus: TELEPHONE_SURVEY_CALL_STATUS | null
	satisfaction: TELEPHONE_SURVEY_SATISFACTION | null
	note: string | null
	calledAt?: string | null
	scheduledCallAt?: string | null
}

export const getTelephoneSurveys = (
	queryParams: ITelephoneSurveyQueryParams,
	onSuccess: (data: ITelephoneSurveysPayload) => void = () => {}
): ThunkResult<Promise<ITelephoneSurveysPayload>> => async (dispatch) => {
	dispatch({ type: LOAD_SELECT_TELEPHONE_SURVEYS.START })
	try {
		const queries = mapValues(queryParams, (value) => value ?? undefined)
		const { data } = await getReq('/api/admin/telephone-surveys', queries)
		const originalData: ITelephoneSurveyOriginalItem[] = data.telephoneSurveys || []
		const tableData: ITelephoneSurveyTableItem[] = map(originalData, (item) => ({
			id: item.id,
			jobAddresses: (item.jobs || []).map((job) => job.address).join(', ') || '-',
			cleanerNames: (item.cleaners || []).map((cleaner) => cleaner.name).join(', ') || '-',
			operatorName: item.operator?.name || '-',
			callStatus: item.callStatus,
			satisfaction: item.satisfaction,
			note: item.note || '',
			calledAt: item.calledAt,
			scheduledCallAt: item.scheduledCallAt,
			trusteeName: item.trusteeName,
			trusteePhone: item.trusteePhone,
			trusteeUserID: item.trusteeUserID,
			organizationName: item.organization?.name || null,
			jobIDs: item.jobIDs || [],
			jobs: item.jobs || [],
			cleaners: item.cleaners || []
		}))

		const payload: ITelephoneSurveysPayload = {
			originalData,
			tableData,
			pagination: data.context || null
		}
		dispatch({ type: LOAD_SELECT_TELEPHONE_SURVEYS.DONE, payload })
		onSuccess(payload)
		return payload
	} catch (error) {
		dispatch({ type: LOAD_SELECT_TELEPHONE_SURVEYS.FAILED })
		return Promise.reject(error)
	}
}

export const getTelephoneSurvey = (
	id: number,
	onSuccess: (data: ITelephoneSurveyOriginalItem) => void = () => {},
	onFailure: (error: any) => void = () => {}
): ThunkResult<Promise<ITelephoneSurveyPayload>> => async (dispatch) => {
	dispatch({ type: LOAD_TELEPHONE_SURVEY.START })
	try {
		const { data } = await getReq(`/api/admin/telephone-surveys/${id}`)
		const payload = { data }
		dispatch({ type: LOAD_TELEPHONE_SURVEY.DONE, payload })
		onSuccess(data)
		return payload
	} catch (error) {
		dispatch({ type: LOAD_TELEPHONE_SURVEY.FAILED })
		onFailure(error)
		return Promise.reject(error)
	}
}

export const createTelephoneSurvey = (
	values: ITelephoneSurveyForm,
	onSuccess: (id: number) => void = () => {},
	onFailure: (error: any) => void = () => {}
): ThunkResult<Promise<boolean>> => async (dispatch) => {
	dispatch({ type: CREATE_TELEPHONE_SURVEY.START })
	try {
		const normalizedValues = mapValues(values, (value, key) => {
			if (key === 'jobIDs') return value
			return value || undefined
		})
		const { data } = await postReq('/api/admin/telephone-surveys', null, normalizedValues)
		dispatch({ type: CREATE_TELEPHONE_SURVEY.DONE })
		onSuccess(get(data, 'id'))
		return true
	} catch (error) {
		dispatch({ type: CREATE_TELEPHONE_SURVEY.FAILED })
		onFailure(error)
		return false
	}
}

export const updateTelephoneSurvey = (
	id: number,
	values: ITelephoneSurveyForm,
	onSuccess: () => void = () => {},
	onFailure: (error: any) => void = () => {}
): ThunkResult<Promise<boolean>> => async (dispatch) => {
	dispatch({ type: CREATE_TELEPHONE_SURVEY.START })
	try {
		const normalizedValues = mapValues(values, (value, key) => {
			if (key === 'jobIDs') return value
			return value || undefined
		})
		await putReq(`/api/admin/telephone-surveys/${id}`, null, normalizedValues)
		dispatch({ type: CREATE_TELEPHONE_SURVEY.DONE })
		onSuccess()
		return true
	} catch (error) {
		dispatch({ type: CREATE_TELEPHONE_SURVEY.FAILED })
		onFailure(error)
		return false
	}
}
